<template>
  <div class="layout">
    <aside class="sidebar" :style="sidebarStyle">
      <div v-if="layoutSettings.showLogo" class="logo" :style="logoStyle">
        <span v-if="!logoImageUrl" class="logo-mark">TA</span>
        <img v-else :src="logoImageUrl" alt="logo" class="logo-image">
        <span v-if="!isCompactSidebar" class="logo-text">触点z7</span>
      </div>
      <el-menu
        :default-active="$route.path"
        :background-color="sidebarBackground"
        :text-color="sidebarTextColor"
        :active-text-color="themeColor"
        :collapse="isCompactSidebar"
        router
      >
        <menu-node
          v-for="item in menuList"
          :key="item.path"
          :item="item"
        />
      </el-menu>
    </aside>

    <section class="main">
      <header class="topbar" :class="{ fixed: layoutSettings.fixedHeader }" :style="topbarStyle">
        <div>{{ pageTitle }}</div>
        <div class="topbar-right">
          <el-popover
            v-if="canViewMessageCenter"
            placement="bottom-end"
            width="360"
            trigger="click"
            popper-class="message-popover"
            @show="handleMessagePopoverShow"
          >
            <div class="message-popover-panel">
              <div class="message-popover-head">
                <span>消息提醒</span>
                <div class="message-popover-actions">
                  <el-button type="text" @click="markAllMessagesRead">全部已读</el-button>
                  <el-button type="text" @click="goMessageCenter">查看全部</el-button>
                </div>
              </div>

              <div v-if="recentMessageLoading" class="message-popover-loading">加载中...</div>
              <div v-else-if="recentMessages.length === 0" class="message-popover-empty">暂无消息</div>
              <div v-else class="message-popover-list">
                <div
                  v-for="item in recentMessages"
                  :key="item.id"
                  class="message-popover-item"
                  @click="openAdminMessage(item)"
                >
                  <span v-if="!item.isRead" class="message-popover-dot"></span>
                  <div class="message-popover-body">
                    <div class="message-popover-title">{{ item.title || '系统消息' }}</div>
                    <div class="message-popover-content">{{ item.content || '-' }}</div>
                    <div class="message-popover-time">{{ item.createdAt || '-' }}</div>
                  </div>
                </div>
              </div>
            </div>

            <el-badge
              slot="reference"
              :value="messageBadgeValue"
              :hidden="!messageUnreadCount"
              class="message-badge"
            >
              <div class="message-trigger">
                <i class="el-icon-bell"></i>
              </div>
            </el-badge>
          </el-popover>
          <el-dropdown trigger="click" @command="handleCommand">
            <div class="user-dropdown">
              <span class="user-name">{{ userLabel }}</span>
              <i class="el-icon-arrow-down el-icon--right user-arrow"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">个人中心</el-dropdown-item>
              <el-dropdown-item command="layout-settings">布局设置</el-dropdown-item>
              <el-dropdown-item command="lock-screen">锁定屏幕</el-dropdown-item>
              <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <tags-view v-if="layoutSettings.showTagsView" />

      <main class="content">
        <router-view />
      </main>
    </section>
  </div>
</template>

<script>
import { routes } from '../router'
import { hasPermission } from '../utils/auth'
import { hasRoutePermission } from '../utils/permissionRoute'
import TagsView from '../components/TagsView/index.vue'
import {
  getAdminRecentMessages,
  getAdminUnreadMessageCount,
  markAdminAllMessagesRead,
  markAdminMessageRead
} from '../api/opsMessage'

const MenuNode = {
  name: 'MenuNode',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  render(h, context) {
    const item = context ? context.props.item : this.item
    const iconClass = item.icon || 'el-icon-menu'
    if (item.children && item.children.length) {
      return h(
        'el-submenu',
        {
          props: {
            index: item.path
          }
        },
        [
          h(
            'template',
            {
              slot: 'title'
            },
            [
              h('i', { class: iconClass }),
              h('span', [item.title])
            ]
          ),
          ...item.children.map(child =>
            h('menu-node', {
              key: child.path,
              props: {
                item: child
              }
            })
          )
        ]
      )
    }
    return h(
      'el-menu-item',
      {
        props: {
          index: item.path
        }
      },
      [
        h('i', { class: iconClass }),
        h('span', { slot: 'title' }, item.title)
      ]
    )
  }
}
MenuNode.components = {
  MenuNode
}

export default {
  name: 'Layout',
  components: {
    TagsView,
    MenuNode
  },
  data() {
    return {
      messageUnreadCount: 0,
      recentMessages: [],
      recentMessageLoading: false
    }
  },
  computed: {
    adminName() {
      return this.$store.getters.adminName
    },
    canViewMessageCenter() {
      return hasPermission('ops_message_center:view')
    },
    userLabel() {
      const info = this.$store.state.adminInfo || {}
      return info.username || this.adminName || '管理员'
    },
    layoutSettings() {
      return this.$store.getters.layoutSettings
    },
    isCompactSidebar() {
      return this.layoutSettings.sidebarMode === 'compact'
    },
    sidebarBackground() {
      return this.layoutSettings.sidebarTheme === 'light' ? '#ffffff' : '#1f2d3d'
    },
    sidebarTextColor() {
      return this.layoutSettings.sidebarTheme === 'light' ? '#303133' : '#c0c4cc'
    },
    sidebarStyle() {
      return {
        width: this.isCompactSidebar ? '64px' : '220px',
        background: this.sidebarBackground
      }
    },
    themeColor() {
      return this.layoutSettings.themeColor || '#409EFF'
    },
    topbarStyle() {
      if (this.layoutSettings.topbarStyle === 'dark') {
        return {
          background: '#1f2d3d',
          color: '#f5f7fa'
        }
      }
      return {
        background: '#fff',
        color: '#303133'
      }
    },
    logoStyle() {
      return {
        color: this.layoutSettings.sidebarTheme === 'light' ? '#303133' : '#ffffff'
      }
    },
    logoImageUrl() {
      return this.layoutSettings.logoImageUrl || ''
    },
    pageTitle() {
      return this.$route.meta.title || '平台管理端'
    },
    messageBadgeValue() {
      if (!this.messageUnreadCount) {
        return 0
      }
      return this.messageUnreadCount > 99 ? '99+' : this.messageUnreadCount
    },
    menuList() {
      const root = routes.find(item => item.path === '/')
      if (!root || !root.children) {
        return []
      }
      return this.buildMenu(root.children, '')
    }
  },
  created() {
    if (this.canViewMessageCenter) {
      this.refreshTopbarMessages()
    }
  },
  methods: {
    buildMenu(routes, parentPath) {
      return routes
        .filter(item => !item.meta || !item.meta.hidden)
        .map(item => {
          const currentPath = item.path.startsWith('/')
            ? item.path
            : `${parentPath}/${item.path}`.replace(/\/+/g, '/')
          const children = Array.isArray(item.children) ? this.buildMenu(item.children, currentPath) : []
          if (children.length > 0) {
            return {
              path: currentPath,
              title: item.meta && item.meta.title,
              icon: item.meta && item.meta.icon,
              children
            }
          }
          if (!hasRoutePermission(item, this.$store.getters.permissionCodes)) {
            return null
          }
          return {
            path: currentPath,
            title: item.meta && item.meta.title,
            icon: item.meta && item.meta.icon
          }
        })
        .filter(Boolean)
    },
    handleCommand(command) {
      if (command === 'logout') {
        this.logout()
        return
      }
      if (command === 'lock-screen') {
        this.$router.push('/lock-screen')
        return
      }
      this.$router.push(`/${command}`)
    },
    logout() {
      this.$store.dispatch('logout')
      this.$router.replace('/login')
    },
    async refreshTopbarMessages() {
      if (!this.canViewMessageCenter) {
        return
      }
      await Promise.all([
        this.fetchUnreadCount(),
        this.fetchRecentMessages()
      ])
    },
    async fetchUnreadCount() {
      const data = await getAdminUnreadMessageCount().catch(() => ({ unreadCount: 0 }))
      this.messageUnreadCount = data && data.unreadCount ? data.unreadCount : 0
    },
    async fetchRecentMessages() {
      this.recentMessageLoading = true
      const data = await getAdminRecentMessages().catch(() => [])
      this.recentMessages = Array.isArray(data) ? data : []
      this.recentMessageLoading = false
    },
    handleMessagePopoverShow() {
      this.fetchRecentMessages()
      this.fetchUnreadCount()
    },
    async markAllMessagesRead() {
      await markAdminAllMessagesRead()
      this.recentMessages = this.recentMessages.map(item => ({
        ...item,
        isRead: 1
      }))
      this.messageUnreadCount = 0
    },
    async openAdminMessage(message) {
      if (!message) {
        return
      }
      if (!message.isRead) {
        await markAdminMessageRead(message.id).catch(() => null)
        message.isRead = 1
        this.messageUnreadCount = Math.max(0, this.messageUnreadCount - 1)
      }
      if (message.jumpPath) {
        this.$router.push(message.jumpPath)
        return
      }
      this.$router.push('/ops/message-center')
    },
    goMessageCenter() {
      this.$router.push('/ops/message-center')
    }
  },
  watch: {
    '$route.path'() {
      if (this.canViewMessageCenter) {
        this.fetchUnreadCount()
      }
    }
  }
}
</script>

<style scoped>
.layout {
  display: flex;
  height: 100vh;
}

.sidebar {
  color: #fff;
  overflow: auto;
  transition: width 0.2s ease;
  border-right: 1px solid rgba(0, 0, 0, 0.04);
}

.logo {
  height: 60px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
  font-size: 18px;
  font-weight: 700;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.logo-mark {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--theme-color);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.logo-image {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.18);
}

.logo-text {
  font-size: 16px;
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.topbar {
  height: 60px;
  padding: 0 20px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 10;
}

.topbar.fixed {
  position: sticky;
  top: 0;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 18px;
}

.message-badge {
  display: flex;
  align-items: center;
}

.message-trigger {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  cursor: pointer;
}

.message-trigger:hover {
  background: rgba(0, 0, 0, 0.04);
}

.message-popover-panel {
  display: flex;
  flex-direction: column;
  max-height: 420px;
  overflow: hidden;
}

.message-popover-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.message-popover-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-popover-loading,
.message-popover-empty {
  padding: 28px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.message-popover-list {
  flex: 1;
  min-height: 0;
  max-height: 360px;
  padding-top: 8px;
  overflow-x: hidden;
  overflow-y: auto;
}

.message-popover-item {
  position: relative;
  display: flex;
  gap: 10px;
  padding: 12px 0;
  cursor: pointer;
  border-bottom: 1px solid #f2f6fc;
}

.message-popover-item:last-child {
  border-bottom: none;
}

.message-popover-dot {
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: #f56c6c;
  flex-shrink: 0;
}

.message-popover-body {
  min-width: 0;
  flex: 1;
}

.message-popover-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.message-popover-content {
  margin-top: 6px;
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.message-popover-time {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}

.user-dropdown {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: inherit;
}

.user-name {
  font-size: 14px;
  color: inherit;
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-arrow {
  color: #909399;
  font-size: 12px;
}

.content {
  flex: 1;
  padding: 20px;
  overflow: auto;
  max-width: 100%;
}

:deep(.el-menu) {
  border-right: none;
}

:deep(.el-menu-item) {
  height: 50px;
  line-height: 50px;
}

:deep(.el-menu-item.is-active) {
  background: rgba(64, 158, 255, 0.14) !important;
  font-weight: 600;
}

:deep(.el-menu-item:hover) {
  background: rgba(64, 158, 255, 0.08) !important;
}
</style>

<style>
.message-popover {
  padding: 12px 16px;
  box-sizing: border-box;
  overflow: hidden;
}

.message-popover .message-popover-panel {
  display: flex;
  flex-direction: column;
  max-height: 420px;
  overflow: hidden;
}

.message-popover .message-popover-list {
  flex: 1;
  min-height: 0;
  max-height: 360px;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
